import { cognitoConfig } from './config';

export interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
}

export interface UserInfo {
  sub: string;
  email: string;
  role?: string;
  isAdmin?: boolean;
}

/**
 * Generates the Google OAuth sign-in URL
 * @param redirectPath - Optional path to redirect to after auth (defaults to /admin/callback)
 */
export function getGoogleSignInUrl(redirectPath = '/admin/callback'): string {
  const redirectUri = `${window.location.origin}${redirectPath}`;
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: cognitoConfig.clientId,
    redirect_uri: redirectUri,
    identity_provider: 'Google',
    scope: 'email openid profile',
  });

  return `https://${cognitoConfig.domain}.auth.${cognitoConfig.region}.amazoncognito.com/oauth2/authorize?${params}`;
}

/**
 * Exchanges authorization code for tokens
 * @param redirectPath - Optional path used during auth (must match the one used in getGoogleSignInUrl)
 */
export async function exchangeCodeForTokens(code: string, redirectPath = '/admin/callback'): Promise<AuthTokens> {
  const redirectUri = `${window.location.origin}${redirectPath}`;
  const params = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: cognitoConfig.clientId,
    code,
    redirect_uri: redirectUri,
  });

  const tokenEndpoint = `https://${cognitoConfig.domain}.auth.${cognitoConfig.region}.amazoncognito.com/oauth2/token`;

  const response = await fetch(tokenEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: params.toString(),
  });

  if (!response.ok) {
    throw new Error('Failed to exchange code for tokens');
  }

  const data = await response.json();

  return {
    accessToken: data.access_token,
    idToken: data.id_token,
    refreshToken: data.refresh_token,
  };
}

/**
 * Decodes a JWT token to extract user info
 */
export function decodeToken(token: string): UserInfo {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join('')
  );

  const payload = JSON.parse(jsonPayload);

  return {
    sub: payload.sub,
    email: payload.email || payload['cognito:username'] || '',
  };
}

/**
 * Stores tokens in localStorage
 */
export function storeTokens(tokens: AuthTokens): void {
  localStorage.setItem('accessToken', tokens.accessToken);
  localStorage.setItem('idToken', tokens.idToken);
  localStorage.setItem('refreshToken', tokens.refreshToken);
}

/**
 * Retrieves stored tokens
 */
export function getStoredTokens(): AuthTokens | null {
  const accessToken = localStorage.getItem('accessToken');
  const idToken = localStorage.getItem('idToken');
  const refreshToken = localStorage.getItem('refreshToken');

  if (!accessToken || !idToken || !refreshToken) {
    return null;
  }

  return { accessToken, idToken, refreshToken };
}

/**
 * Clears stored tokens
 */
export function clearTokens(): void {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('idToken');
  localStorage.removeItem('refreshToken');
}

/**
 * Generates the logout URL
 */
export function getLogoutUrl(): string {
  const params = new URLSearchParams({
    client_id: cognitoConfig.clientId,
    logout_uri: `${window.location.origin}/admin`,
  });

  return `https://${cognitoConfig.domain}.auth.${cognitoConfig.region}.amazoncognito.com/logout?${params}`;
}

/**
 * Checks if the access token is expired
 */
export function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(
      atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
    );

    if (!payload.exp) {
      return true;
    }

    // Check if token expires in the next 5 minutes
    const expiresAt = payload.exp * 1000;
    const now = Date.now();
    return expiresAt - now < 5 * 60 * 1000;
  } catch {
    return true;
  }
}
